// apps/spa/src/hooks/useCommands.ts
// TanStack Query wrappers for /api/commands CRUD + retry-registration.
//
// Surface (CMD-01..CMD-04):
//   useCommands()          — list view with auto-poll while any row is Registering
//   useCommand(id)         — edit form pre-fill
//   useCreateCommand()     — POST /api/commands (full body)
//   useUpdateCommand(id)   — PUT /api/commands/:id (full replace)
//   useDeleteCommand()     — DELETE /api/commands/:id
//   useToggleCommand()     — PATCH /api/commands/:id { enabled } (optimistic update)
//   useRetryRegistration() — POST /api/commands/:id/retry-registration
//
// Auto-poll logic (RESEARCH OQ #1):
//   When any enabled command has registeredAt=null and lastRegisterError=null,
//   it's in the "Registering…" liminal state — background waitUntil hasn't settled.
//   The hook polls every 2s until all rows settle. Stops automatically.
//
// Optimistic update pattern for useToggleCommand mirrors useToggleReminder.ts.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import type { SlashCommand } from '@guardian/shared';

// Wire body for POST + PUT
// Phase 7 (07-05): type field added; embedJson is optional (omitted for non-static types
// per guardian-worker's type-gate — routes/commands.ts skips embed validation for non-static).
export type CommandWriteBody = {
  name: string;
  description: string;
  type?: string;
  embedJson?: string;
  enabled?: boolean;
};

// ---------------------------------------------------------------------------
// List — auto-polls while any row is in Registering state
// ---------------------------------------------------------------------------

export function useCommands() {
  return useQuery({
    queryKey: ['commands'] as const,
    queryFn: () => api<SlashCommand[]>('/api/commands'),
    refetchInterval: (query) => {
      const data = query.state.data as SlashCommand[] | undefined;
      // Auto-poll every 2s while any enabled command is in the Registering liminal state
      // (registeredAt null AND lastRegisterError null AND enabled true).
      const hasRegistering = data?.some(
        (cmd) => cmd.enabled && !cmd.registeredAt && !cmd.lastRegisterError,
      );
      return hasRegistering ? 2000 : false;
    },
  });
}

// ---------------------------------------------------------------------------
// Get one — for edit form pre-fill
// ---------------------------------------------------------------------------

export function useCommand(id: string | undefined) {
  return useQuery({
    queryKey: ['commands', id] as const,
    queryFn: () => api<SlashCommand>(`/api/commands/${id}`),
    enabled: !!id,
  });
}

// ---------------------------------------------------------------------------
// Create — POST /api/commands
// ---------------------------------------------------------------------------

export function useCreateCommand() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CommandWriteBody) =>
      api<SlashCommand>('/api/commands', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['commands'] });
    },
  });
}

// ---------------------------------------------------------------------------
// Update — PUT /api/commands/:id (full replace)
// ---------------------------------------------------------------------------

export function useUpdateCommand(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CommandWriteBody) =>
      api<SlashCommand>(`/api/commands/${id}`, {
        method: 'PUT',
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['commands'] });
      qc.invalidateQueries({ queryKey: ['commands', id] });
    },
  });
}

// ---------------------------------------------------------------------------
// Delete — DELETE /api/commands/:id
// ---------------------------------------------------------------------------

export function useDeleteCommand() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api(`/api/commands/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      toast.success('✓ Command deleted');
      qc.invalidateQueries({ queryKey: ['commands'] });
    },
    onError: () => {
      toast.error('Could not delete command. Try again.');
    },
  });
}

// ---------------------------------------------------------------------------
// Toggle — PATCH /api/commands/:id { enabled } with optimistic update
// ---------------------------------------------------------------------------

export type ToggleCommandVars = { id: string; enabled: boolean };

export function useToggleCommand() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, enabled }: ToggleCommandVars) =>
      api<SlashCommand>(`/api/commands/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ enabled }),
      }),
    onMutate: async ({ id, enabled }) => {
      // Cancel in-flight refetches so optimistic write isn't overwritten.
      await qc.cancelQueries({ queryKey: ['commands'] });
      const previous = qc.getQueryData<SlashCommand[]>(['commands']);
      qc.setQueryData<SlashCommand[]>(['commands'], (old) =>
        old?.map((cmd) => (cmd.id === id ? { ...cmd, enabled } : cmd)),
      );
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) {
        qc.setQueryData(['commands'], ctx.previous);
      }
      toast.error('Could not update command. Try again.');
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['commands'] });
    },
  });
}

// ---------------------------------------------------------------------------
// Retry registration — POST /api/commands/:id/retry-registration
// ---------------------------------------------------------------------------

export function useRetryRegistration() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api(`/api/commands/${id}/retry-registration`, { method: 'POST' }),
    onSuccess: () => {
      toast.success('Registration re-triggered. Status will update shortly.');
      qc.invalidateQueries({ queryKey: ['commands'] });
    },
    onError: () => {
      toast.error('Could not retry registration. Try again.');
    },
  });
}
